import { format, startOfMonth, intervalToDuration, formatDuration, formatDistanceStrict } from 'date-fns';
import { enGB, pl } from 'date-fns/locale';
import { i18n, getLanguageFromLocale, getEffectiveLocale } from '../i18n/i18n';

// mapa (poza funkcją), aby cache żył między wywołaniami:
const singleFormMapCache = new Map(); // key: localeCodeOrRef -> value: map

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function getCurrentLocale() {
    const locales = {
        'en-GB': enGB,
        pl,
    };
    const lang = getLanguageFromLocale(getEffectiveLocale());
    const currentLocaleConfig = locales[lang] || locales['en-GB'];
    return currentLocaleConfig;
}

export default {
    createYearOptions({ from, to = new Date().getUTCFullYear() }) {
        const options = [];
        for (let year = from; year <= to; year += 1) {
            options.push({
                text: year,
                value: year,
            });
        }
        return options.reverse();
    },

    createMonthOptions() {
        const options = [];
        for (let month = 0; month <= 11; month += 1) {
            options.push({
                text: this.getMonthName(month),
                value: month + 1,
            });
        }
        return options;
    },

    getMonthName(monthNo) {
        const date = startOfMonth(new Date());
        date.setMonth(monthNo);
        return capitalizeFirstLetter(i18n.d(date, 'monthName'));
    },

    formatDate(value, dateFormat = 'dd.MM.yyyy') {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, dateFormat || 'dd.MM.yyyy', { locale: getCurrentLocale() });
    },

    formatDateTime(value, dateFormat = 'dd.MM.yyyy HH:mm:ss') {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, dateFormat || 'dd.MM.yyyy HH:mm:ss', { locale: getCurrentLocale() });
    },

    formatHour(value, timeFormat = 'HH:mm') {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, timeFormat || 'HH:mm', { locale: getCurrentLocale() });
    },

    /**
     * Converts seconds into a formatted string value
     * @param value
     * @return {string}
     */
    secondsToString(value) {
        let seconds = value;
        const days = Math.floor(seconds / (3600 * 24));
        seconds -= days * 3600 * 24;
        const hours = Math.floor(seconds / 3600);
        seconds -= hours * 3600;
        const minutes = Math.floor(seconds / 60);
        seconds -= minutes * 60;
        seconds = Math.round(seconds);

        const parts = [];
        if (days > 0) {
            parts.push(`${days}${i18n.t('timeShort.days')}`);
        }
        if (hours > 0) {
            parts.push(`${hours}${i18n.t('timeShort.hours')}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes}${i18n.t('timeShort.minutes')}`);
        }
        if (seconds > 0) {
            parts.push(`${seconds}${i18n.t('timeShort.seconds')}`);
        }

        return parts.length > 0 ? parts.join(' ') : `0${i18n.t('timeShort.seconds')}`;
    },

    formatMonth(value) {
        const date = value instanceof Date ? value : new Date(value);
        return this.getMonthName(date.getMonth());
    },

    formatDateForUrl(value) {
        const date = value instanceof Date ? value : new Date(value);
        return format(date, 'yyyy-MM-dd');
    },

    /**
     * Zwraca opis różnicy czasu między datami.
     * Obsługuje zaokrąglanie i locale (date-fns lub string BCP-47).
     */
    getDurationBetweenDates(startDate, endDate, options = {}) {
        const { roundToUnit, roundingMethod = 'ceil', locale: localeOption } = options;

        // 1) Uporządkowanie kolejności
        let start = startDate;
        let end = endDate;
        if (start > end) {
            [start, end] = [end, start]; // eslint-disable-line no-param-reassign
        }

        // 2) Pobranie lokalizacji (date-fns locale lub string BCP-47)
        const locale = localeOption ?? (typeof getCurrentLocale === 'function' ? getCurrentLocale() : undefined);

        // 3) Wczesny zwrot dla identycznych dat
        if (Number(start) === Number(end)) {
            return formatDuration({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }, { locale });
        }

        // --- Helpery wewnętrzne ---

        // Klucz cache: jeśli mamy obiekt date-fns, użyj wskaźnika obiektu; jeśli string – użyj stringa.
        const getLocaleCacheKey = loc => (typeof loc === 'object' && loc ? loc : typeof loc === 'string' ? loc : 'default'); // eslint-disable-line no-nested-ternary

        // Buduje mapę "goły wyraz" -> "1 <wyraz>" na podstawie date-fns locale (z cache)
        const getSingleFormMap = () => {
            const cacheKey = getLocaleCacheKey(locale);
            const cached = singleFormMapCache.get(cacheKey);
            if (cached) return cached;

            // locale może być stringiem lub obiektem date-fns; tu potrzebujemy obiektu z .formatDistance
            const localeObject = typeof locale === 'object' && locale && typeof locale.formatDistance === 'function' ? locale : enGB; // bezpieczny fallback

            const tokens = ['xYears', 'xMonths', 'xWeeks', 'xDays', 'xHours', 'xMinutes', 'xSeconds'];
            const map = {};
            for (const token of tokens) {
                const word = localeObject.formatDistance(token, 1, { addSuffix: false });
                const bare = String(word || '').trim(); // np. "rok", "miesiąc", "dzień", ...
                if (bare) map[bare] = `1 ${bare}`;
            }

            singleFormMapCache.set(cacheKey, map);
            return map;
        };

        // Mapowanie form 1 (używane w obu ścieżkach) — z cache
        const singleFormMap = getSingleFormMap();

        // Zastępuje "goły wyraz" -> "1 <wyraz>", działa i dla listy z delimiterem
        const applySingleFormPatch = text => {
            if (!text) return text;
            // Używamy tego samego delimiteru co w formatDuration niżej (', ')
            if (text.includes(', ')) {
                return text
                    .split(', ')
                    .map(part => singleFormMap[part] || part)
                    .join(', ');
            }
            return singleFormMap[text] || text;
        };

        // 4) Tryb: zaokrąglanie do określonej jednostki (np. "13 dni")
        if (roundToUnit) {
            const roundedText = formatDistanceStrict(start, end, {
                unit: roundToUnit,
                roundingMethod,
                locale,
                addSuffix: false,
            });
            return applySingleFormPatch(roundedText);
        }

        // 5) Tryb: pełny rozkład
        const duration = intervalToDuration({ start, end });

        let formatted = formatDuration(duration, {
            locale,
            delimiter: ', ',
            format: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
            zero: true,
        });

        if (!formatted) {
            formatted = formatDuration({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }, { locale });
        }

        return applySingleFormPatch(formatted);
    },
};
